"use client";

import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { resolveStopLossIcon } from "@/lib/stopLossIcons";

const ACCENT_HEX = {
  amber: "#F59E0B",
  blue: "#3B82F6",
  green: "#22C55E",
};

const FALLBACK_PACKAGES = [
  {
    id: "silver",
    planType: "Silver",
    label: "Starter Shield",
    price: 500,
    premium: 15,
    description:
      "Protects a share of your principal. Ideal for new investors wanting a safety net on small positions.",
    features: [
      "Principal protection (premium %)",
      "Valid for all plans",
      "Activates on early exit",
    ],
    accentColor: "amber",
    iconKey: "shield",
    isPopular: false,
  },
  {
    id: "gold",
    planType: "Gold",
    label: "Pro Shield",
    price: 1200,
    premium: 25,
    description:
      "Stronger coverage. The most popular choice for mid-range portfolio holders.",
    features: [
      "Higher principal protection",
      "Priority claim processing",
      "Valid for all plans",
    ],
    accentColor: "blue",
    iconKey: "shieldCheck",
    isPopular: true,
  },
  {
    id: "platinum",
    planType: "Platinum",
    label: "Elite Shield",
    price: 2000,
    premium: 50,
    description:
      "Maximum coverage for serious investors and larger positions.",
    features: [
      "Top-tier principal protection",
      "Priority claim processing",
      "Dedicated support agent",
    ],
    accentColor: "green",
    iconKey: "shieldAlert",
    isPopular: false,
  },
];

function accentToHex(accent) {
  return ACCENT_HEX[accent] || ACCENT_HEX.amber;
}

export default function StopLoss() {
  const [packages, setPackages] = useState(FALLBACK_PACKAGES);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch("/api/public/stop-loss-packages");
      const data = await r.json().catch(() => ({}));
      if (r.ok && Array.isArray(data.packages) && data.packages.length > 0) {
        setPackages(data.packages);
      } else {
        setPackages(FALLBACK_PACKAGES);
      }
    } catch {
      setPackages(FALLBACK_PACKAGES);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section
      id="stop-loss"
      className="bg-bg-primary 2xl:px-6 xl:px-5 lg:px-4 md:px-4  px-4 2xl:py-24 xl:py-20 lg:py-16 md:py-12 py-10"
    >
      <div className="mx-auto max-w-[1100px]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16 text-center"
        >
          <span className="mb-4 inline-block rounded-full border border-accent-amber/25 bg-accent-amber/10 px-4 py-1.5 text-xs font-medium text-accent-amber sm:text-sm">
            Stop-Loss Protection
          </span>
          <h2 className="font-display mb-4 text-[clamp(32px,5vw,56px)]">
            Protect Your <span className="text-ember">Principal</span>
          </h2>
          <p className="mx-auto max-w-[580px] text-base leading-relaxed text-text-muted sm:text-lg">
            Our Stop-Loss feature safeguards a percentage of your investment in
            the event of early withdrawal or market disruption. Choose a
            protection tier that fits your risk profile.
          </p>
        </motion.div>

        {loading ? (
          <div className="flex min-h-[280px] items-center justify-center rounded-xl border border-border bg-bg-card">
            <div className="flex items-center gap-2 text-sm text-text-muted">
              <Loader2 className="h-5 w-5 animate-spin text-accent-amber" />
              Loading protection tiers…
            </div>
          </div>
        ) : packages.length === 0 ? (
          <div className="rounded-xl border border-border bg-bg-card p-8 text-center text-sm text-text-muted">
            Stop-loss tiers will appear here once they are configured in the admin
            panel.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {packages.map((pkg, i) => {
              const color = accentToHex(pkg.accentColor);
              const Icon = resolveStopLossIcon(pkg.iconKey);
              const amount = `$${Number(pkg.price || 0).toLocaleString()}`;
              const coverage = `${Number(pkg.premium || 0)}%`;
              const key = pkg.id || pkg.planType || pkg.label || String(i);

              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.12 }}
                  className="card-base"
                  style={{
                    borderColor: pkg.isPopular ? `${color}60` : "var(--border)",
                  }}
                >
                  <div className="relative overflow-hidden p-4 2xl:p-8 xl:p-7 lg:p-6 md:p-5 sm:p-4">
                    {pkg.isPopular ? (
                      <div
                        style={{
                          background: `${color}20`,
                          border: `1px solid ${color}40`,
                        }}
                        className="absolute right-4 top-4 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.06em]"
                      >
                        Most Popular
                      </div>
                    ) : null}

                    <div
                      style={{
                        background: `radial-gradient(circle, ${color}0a 0%, transparent 70%)`,
                      }}
                      className="pointer-events-none absolute right-0 top-0 h-[200px] w-[200px]"
                    />

                    <div
                      style={{
                        background: `${color}18`,
                        border: `2px solid ${color}35`,
                        boxShadow: `0 0 20px ${color}20`,
                      }}
                      className="relative mb-5 flex h-[60px] w-[60px] items-center justify-center rounded-full"
                    >
                      <Icon size={26} color={color} />
                    </div>

                    <div
                      className="font-display mb-1 text-5xl leading-none font-extrabold"
                      style={{ color }}
                    >
                      {coverage}
                    </div>
                    <div className="mb-4 text-xs text-text-muted sm:text-sm">
                      Premium on protected investment
                    </div>

                    <div className="font-display mb-3 text-lg font-bold text-text-primary">
                      {pkg.label}
                      <span className="ml-2.5 text-sm" style={{ color }}>
                        ({amount})
                      </span>
                    </div>

                    <p className="mb-6 text-sm leading-relaxed text-text-muted">
                      {pkg.description}
                    </p>

                    <div className="flex flex-col gap-2.5">
                      {(pkg.features || []).map((f, fi) => (
                        <div key={`${key}-f-${fi}`} className="flex items-center gap-2.5">
                          <div
                            style={{ background: color }}
                            className="h-1.5 w-1.5 shrink-0 rounded-full"
                          />
                          <span className="text-xs text-text-muted sm:text-sm">
                            {f}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

      </div>
    </section>
  );
}
